import { HttpClient } from '@angular/common/http';
import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, catchError, map, of, retry, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Channel, Message, MessageResponse, SendMessageResponse, UserProfile } from '../dto';
import { AbstractService } from './abstractservice';
import { ApplicationService } from './application.service';
import { NotificationService } from './notification.service';

@Injectable({
    providedIn: 'root'
})
export abstract class BaseMessagingService extends AbstractService {

    // Two-level state: this outer signal represents "a channel was added/removed", each inner
    // per-channel WritableSignal represents "this channel's own messages changed" - kept as two
    // levels so a message arriving in channel A doesn't have to notify every subscriber watching
    // channel B. Writes to either level must produce a new Map/Channel reference rather than
    // mutate in place - a signal's default write compares by reference, unlike a BehaviorSubject.
    private readonly allChannelsMapState: WritableSignal<Map<number, WritableSignal<Channel>>> = signal(new Map());
    public readonly allChannelsMapSignal: Signal<Map<number, WritableSignal<Channel>>> = this.allChannelsMapState.asReadonly();
    protected readonly allChannelsMap$: Observable<Map<number, WritableSignal<Channel>>> = toObservable(this.allChannelsMapState);

    // Reading channelSignal() while building this array registers each individual channel's
    // signal as a computed() dependency too, not just the outer map - so this recomputes both
    // when a channel is added/removed and when any already-loaded channel's own content changes.
    public readonly allChannelsFlatSignal: Signal<Channel[]> = computed(() =>
        Array.from(this.allChannelsMapState().values()).map(channelSignal => channelSignal()));
    private readonly allChannelsFlat$: Observable<Channel[]> = toObservable(this.allChannelsFlatSignal);

    constructor(protected coreAppService: ApplicationService, protected http: HttpClient, protected notificationService: NotificationService) {
        super(coreAppService.authService, coreAppService.featureFlagService);
    }

    public getObservableChannels(): Observable<Channel[]> {
        return this.allChannelsFlat$;
    }

    /**
     * HTTP Helper function and primary view function: send a message and insert it in the view appropriately.
     * Updates the Channel's own signal.
     */
    sendMessage(message: Message): void {
        this.http.post<SendMessageResponse>(environment.apiUrl + '/chat/message/', message, this.httpOptionsAuthJson())
            .pipe(
                retry(2),
                catchError(this.handleError),
            ).subscribe(sendResponse => {
                let activeUser = this.coreAppService.userService.activeUser$.getValue();
                message.IsMyMessage = true;
                message.SenderUsername = activeUser.Username;
                message.SendDate = new Date(sendResponse.SendDate);
                message.Id = sendResponse.MessageId;
                // Use the server-materialized text/subject rather than what was typed, so a live
                // send renders identically to a subsequent refetch (e.g. moderation redaction).
                message.MessageText = sendResponse.MessageText;
                message.Subject = sendResponse.Subject;
                message.IsPartiallyBlocked = !!sendResponse.IsPartiallyBlocked;
                message.OriginalMessageText = sendResponse.OriginalMessageText;
                message.OriginalSubject = sendResponse.OriginalSubject;
                message.IsFullyBlocked = !!sendResponse.IsFullyBlocked;
                // insertMessagesToChannel is cold (it now runs the SenderProfile hydration fetch
                // as part of its own pipeline) - it does nothing at all until subscribed.
                this.insertMessagesToChannel([message]).subscribe();
            });
    }

    /**
     * Retrieves a page of messages from the server and adds them to their viewable parent channel.
     * If fetchNewest is false, 100 messages will be fetched backwards in time from the oldest currently loaded message, otherwise only fetches
     * newer messages that have occurred after initial page load.
     */
    public getMessagesPage(fetchNewest: boolean, channelId: number | null, publicOrPrivate: string | null): Observable<MessageResponse> {
        let lastMessageId = null;
        if (channelId && !fetchNewest) {
            let channel = this.allChannelsMapState().get(channelId)?.();
            if (channel) {
                lastMessageId = channel.Messages[channel.Messages.length - 1].Id;
            }
        }
        return this.retrieveMessages(fetchNewest ? null : lastMessageId, channelId, publicOrPrivate);
    }

    /**
     * HTTP Helper function to create a new channel
     */
    public createChannel(createChannel: Channel, isPrivate: boolean): Observable<Channel> {
        return this.http.post<Channel>(`${environment.apiUrl}/chat/channel/${isPrivate}`, createChannel, this.httpOptionsAuthJson())
            .pipe(
                retry(2),
                map(channel => new Channel(channel, this.coreAppService.userService.activeUser$.getValue().Username)),
                tap(channel => this.updateChannels([channel])),
                catchError(this.handleError),
            );
    }

    /**
     * Creates a new channel and inserts it and sends a new message (if used)
     */
    public createChannelWithInitialMessage(createChannel: Channel, message: Message, isPrivate: boolean): Observable<Channel> {
        return this.createChannel(createChannel, isPrivate).pipe(
            map(channel => new Channel(channel, this.coreAppService.userService.activeUser$.getValue().Username)),
            tap(channel => {
                this.updateChannels([channel]);
                message.ChannelId = channel.Id;
                if (message.MessageText.length > 0) { this.sendMessage(message); };
            }));
    }

    public updateChannels(updatedChannels: Channel[]) {
        let newChannelSignals = updatedChannels
            .map(updatedChannel => [updatedChannel.Id, this.updateAndReportIsChannelNew(updatedChannel)] as [number, WritableSignal<Channel>])
            .filter(newSignal => newSignal[1] != null);

        if (newChannelSignals?.length) {
            // A brand new Map, not the same reference mutated in place - this outer signal's
            // write compares by reference, and adding a channel needs its subscribers notified.
            let map = new Map(this.allChannelsMapState());
            newChannelSignals.forEach(newIdChannel => map.set(newIdChannel[0], newIdChannel[1]));
            this.allChannelsMapState.set(map);
        }
    }

    private updateAndReportIsChannelNew(channel: Channel): WritableSignal<Channel> {
        let channelSignal = this.allChannelsMapState().get(channel.Id);
        if (channelSignal) {
            channel.Messages = this.mergeMessages(channel, channelSignal());
            channel.Messages.forEach(message => channel.ContainsNotification = channel.ContainsNotification || (message.targetingNotification && !message.targetingNotification.ReadDate));
            channelSignal.set(channel);
            return null;
        } else {
            let channelSignal = signal(channel);
            channel.Messages.forEach(message => channel.ContainsNotification = channel.ContainsNotification || (message.targetingNotification && !message.targetingNotification.ReadDate));
            return channelSignal;
        }
    }

    /**
     * HTTP helper method to fetch channels by ID
     */
    public retrieveChannels(publicOrPrivate: string, ...channelIds: number[]): Observable<MessageResponse> {
        const pathParams = (channelIds?.length ? channelIds.join(',') : null) ?? publicOrPrivate;
        return this.http.get<MessageResponse>(environment.apiUrl + `/chat/channel/${pathParams}`, this.httpOptionsAuthJson())
            .pipe(
                map(msgResponse => {
                    let activeUser = this.coreAppService.userService.activeUser$.getValue();
                    msgResponse.Channels = msgResponse.Channels.map(channel => new Channel(channel, activeUser.Username));
                    return msgResponse;
                }),
                switchMap(msgResponse => this.hydrateAndUpdateChannels(msgResponse)),
                retry(2),
                catchError(this.handleError),
            );
    }

    public insertMessagesToChannel(messages: Message[], forceUpdate = false): Observable<{ channel: Channel; messages: Message[]; }> {
        if (!messages?.length) return of();
        return this.hydrateSenderProfiles(messages).pipe(
            switchMap(hydratedMessages => {
                let channelSignal = this.allChannelsMapState().get(hydratedMessages[0].ChannelId);
                let channel = channelSignal?.();
                if (!channel) {
                    return this.retrieveChannels(null, hydratedMessages[0].ChannelId)
                        .pipe(
                            switchMap(newChannel => of({ channel: this.insertToChannelAndUpdateSubjects(newChannel.Channels[0], hydratedMessages, forceUpdate), messages: hydratedMessages })));
                } else {
                    return of({ channel: this.insertToChannelAndUpdateSubjects(channel, hydratedMessages, forceUpdate), messages: hydratedMessages });
                }
            }));
    }

    public markNotificationsRead(...messagesWithNotifications: Message[]): void {
        if (!messagesWithNotifications?.length) return;
        this.notificationService.markNotificationsAsRead(...messagesWithNotifications.map(m => m.targetingNotification)).subscribe(() => {
            let channelMap = this.allChannelsMapState();
            new Set(messagesWithNotifications.map(m => m.ChannelId)).forEach(id => {
                var channelSignal = channelMap.get(id);
                var channel = channelSignal?.();
                channel.ContainsNotification = channel.Messages.findIndex(m => m.targetingNotification && !m.targetingNotification.ReadDate) >= 0;
                // channel is the exact object channelSignal already holds - write a fresh Channel
                // reference (see the field-level comment) so the signal's write is a real change.
                channelSignal.set(Object.assign(new Channel(null, null), channel));
            })
        });
    }

    public markMessagesRead(...messages: Message[]): void {
        if (!messages?.length) return;
        this.http.patch(environment.apiUrl + '/chat/message/', messages.map(n => n.Id), this.httpOptionsAuthJson())
            .subscribe(() => {
                messages.forEach(m => m.Read = true);
                // See sendMessage's comment - insertMessagesToChannel is cold, must be subscribed.
                this.insertMessagesToChannel(messages, true).subscribe();
            });
    }

    // Fills in SenderProfile (see Message's "local hydrated" field comment in dto.ts) for any
    // message that doesn't already carry one. Called on every path a Message can enter a channel
    // through (initial/paginated load, live push, the sender's own optimistic echo) so a
    // message's SenderProfile is always populated by the time it reaches a template - callers
    // should never need to null-check SenderProfile as routine behavior.
    //
    // Fetches every unique SenderUsername in one batched call through the unified repository
    // gateway's cache-first getOrRetrieve. The call is caught as a unit so a failure leaves every
    // message in this batch with a null SenderProfile (the template's fallback icon is the
    // intended path for that) instead of throwing - this call sits ahead of
    // updateChannels/insertToChannelAndUpdateSubjects in the pipelines it's used from, so an
    // uncaught error here would silently stop messages from ever rendering.
    private hydrateSenderProfiles(messages: Message[]): Observable<Message[]> {
        if (!messages?.length) return of(messages ?? []);
        const dehydrated = messages.filter(m => !m.hasBeenRehydrated);
        if (!dehydrated.length) return of(messages);

        const uniqueUsernames = [...new Set(dehydrated.map(m => m.SenderUsername))];
        return this.coreAppService.unifiedRepo.getOrRetrieve(true, true, UserProfile, ...uniqueUsernames)
            .pipe(
                catchError(() => of(<UserProfile[]>[])),
                map(profiles => {
                    const byUsername = new Map(profiles.map(p => [p.Username, p]));
                    dehydrated.forEach(m => {
                        m.SenderProfile = byUsername.get(m.SenderUsername) ?? null;
                        m.hasBeenRehydrated = true;
                    });
                    return messages;
                }));
    }

    // Shared tail for every bulk/paginated fetch (retrieveChannels, retrieveMessages,
    // retrieveMessage, retrieveMessagesFromParent) - hydrates every message in the response
    // before merging it into allChannelsMapState, so a freshly-loaded or newly-paged-in message
    // never renders a beat behind its SenderProfile.
    private hydrateAndUpdateChannels(msgResponse: MessageResponse): Observable<MessageResponse> {
        const allMessages = (msgResponse?.Channels ?? []).flatMap(c => c.Messages ?? []);
        return this.hydrateSenderProfiles(allMessages).pipe(
            tap(() => this.updateChannels(msgResponse.Channels)),
            map(() => msgResponse));
    }

    /** YOU MUST HAVE LOADED THE CHANNEL ALREADY */
    private insertToChannelAndUpdateSubjects(channel: Channel, messages: Message[], forceUpdate = false): Channel {

        if (!channel || !messages?.length) return channel;

        let oldMessages = channel.Messages;

        let nonInsertedMessages = messages.filter(message => {
            if (message.targetingNotification) {
                channel.ContainsNotification = channel.ContainsNotification || (message.targetingNotification && !message.targetingNotification.ReadDate);
            }

            let foundIndex = oldMessages.findIndex(msg => msg.Id === message.Id);
            if (!forceUpdate && foundIndex >= 0) { //no op, no dupes
                return false;
            } else if (foundIndex >= 0) {
                oldMessages[foundIndex] = message; //overwrite, no dupes
                return false;
            }
            return true;
        });

        if (nonInsertedMessages) {
            oldMessages.push(...nonInsertedMessages);
        }
        oldMessages.sort((a, b) => b.Id - a.Id);

        channel.Messages = oldMessages;

        // channel here is frequently the exact object channelSignal already holds (retrieved via
        // a prior channelSignal() read in a caller), mutated above by the push/sort in place -
        // writing it back unchanged would silently produce no update under a signal's default
        // reference-equality write. Clone onto a fresh Channel instance (preserving its
        // prototype) so both the inner and outer writes below are real value changes; the outer
        // map's own identity must change too so getPublicChannels()' raw-map consumers, which
        // don't read through the per-channel signal, still see this update - the prior
        // BehaviorSubject-based AllChannelsSubject$ always re-emitted here regardless of
        // reference identity, this preserves that behavior.
        let updatedChannel = Object.assign(new Channel(null, null), channel);
        let allChannels = this.allChannelsMapState();
        let channelSignal = allChannels.get(updatedChannel.Id);
        channelSignal.set(updatedChannel);
        this.allChannelsMapState.set(new Map(allChannels));
        return updatedChannel;
    }

    private mergeMessages(channelUpdate: Channel, oldChannel: Channel): Message[] {
        if (!channelUpdate || !oldChannel) return [];
        if (!channelUpdate.Messages) { channelUpdate.Messages = []; }
        if (!oldChannel.Messages) { oldChannel.Messages = []; }

        channelUpdate.Messages = channelUpdate.Messages.filter(msg => oldChannel.Messages.findIndex(chMsg => chMsg.Id === msg.Id) === -1); //no duplicates
        channelUpdate.Messages.sort((a, b) => b.Id - a.Id);
        oldChannel.Messages.unshift(...channelUpdate.Messages);
        oldChannel.Messages.sort((a, b) => b.Id - a.Id);

        return oldChannel.Messages;
    }

    /**
     * HTTP helper method to fetch another page of 100 messages starting beginning with the message with lastId and going back in time
     * If lastId is null, fetches the 100 most recent messages, if channel is null fetches 100 messages across all active channels (DMs, messageboards)
     */
    protected retrieveMessages(lastId: number | null, channelId: number | null, publicOrPrivate: string | null): Observable<MessageResponse> {
        if ((channelId == null && publicOrPrivate == null) || (channelId != null && publicOrPrivate != null)) {
            throw "Invalid Argument, needs correct channel OR public/private delineation";
        }
        return this.http.get<MessageResponse>(environment.apiUrl + `/chat/message/${channelId ? `channel/${channelId}/` : ''}` +
            `${publicOrPrivate ? publicOrPrivate + '/' : ''}` +
            `${lastId || channelId ? `page/${lastId ?? ''}` : ''}` +
            `${!lastId && !channelId ? 'all' : ''}`,
            this.httpOptionsAuthJson())
            .pipe(
                map(msgResp => {
                    if (!msgResp) {
                        msgResp = new MessageResponse(null, null);
                    }
                    if (msgResp.Channels) {
                        msgResp.Channels = msgResp.Channels.map(channel => new Channel(channel, this.coreAppService.userService.activeUser$.getValue().Username));
                    } else {
                        msgResp.Channels = [];
                    }
                    return msgResp;
                }),
                switchMap(msgResponse => this.hydrateAndUpdateChannels(msgResponse)),
                retry(2),
                catchError(this.handleError),
            );
    }

    /**
     * HTTP helper method to fetch a thread based on a parent message ID
     */
    private retrieveMessage(...id: number[]): Observable<MessageResponse> {
        return this.http.get<MessageResponse>(environment.apiUrl + `/chat/message/${id.join(',')}`, this.httpOptionsAuthJson())
            .pipe(
                map(msgResp => {
                    let activeUser = this.coreAppService.userService.activeUser$.getValue()
                    msgResp.Channels = msgResp.Channels.map(channel => new Channel(channel, activeUser.Username));
                    return msgResp;
                }),
                switchMap(msgResponse => this.hydrateAndUpdateChannels(msgResponse)),
                retry(2),
                catchError(this.handleError),
            );
    }

    /**
     * HTTP helper method to fetch a thread based on a parent message ID
     */
    private retrieveMessagesFromParent(parentId: number): Observable<MessageResponse> {
        return this.http.get<MessageResponse>(environment.apiUrl + `/chat/message/thread/${parentId}`, this.httpOptionsAuthJson())
            .pipe(
                map(msgResp => {
                    let activeUser = this.coreAppService.userService.activeUser$.getValue()
                    msgResp.Channels = msgResp.Channels.map(channel => new Channel(channel, activeUser.Username));
                    return msgResp;
                }),
                switchMap(msgResponse => this.hydrateAndUpdateChannels(msgResponse)),
                retry(2),
                catchError(this.handleError),
            );
    }


    public static sortChannelsByMessageRecency(allChannels: Channel[]): Channel[] {
        return allChannels.sort((channelA, channelB) => {
            let compareChannelA: number;
            let compareChannelB: number;
            if (channelA.Messages && channelA.Messages.length > 0) {
                compareChannelA = channelA.Messages[0].SendDate.getTime();
            } else {
                compareChannelA = channelA.CreateDate.getTime();
            }
            if (channelB.Messages && channelB.Messages.length > 0) {
                compareChannelB = channelB.Messages[0].SendDate.getTime();
            } else {
                compareChannelB = channelB.CreateDate.getTime();
            }
            return compareChannelB - compareChannelA;
        });
    }

}
